import { ButtonHTMLAttributes, forwardRef, ReactElement } from 'react'
import { Slot, Slottable } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const buttonVariants = cva(
  [
    'group relative rounded-radius-minimal',
    'flex items-center justify-between flex-nowrap px-spacing-sm py-spacing-xs',
    '[&>.icon]:relative [&>*]:flex [&>.icon]:overflow-hidden [&>*]:!-my-[0.36em]',
    'transition-colors duration-500 ease-in-out'
  ],
  {
    variants: {
      variant: {
        primary: ['bg-text-accent-light [&_*]:text-text-button-primary-light [&_svg]:fill-current'],
        secondary: [
          'bg-transparent border border-border-primary-light dark:border-border-primary-light text-text-primary-light [&_svg]:fill-surface-primay-invert-light dark:[&_svg]:fill-surface-primay-invert-light'
        ]
      }
    },
    defaultVariants: {
      variant: 'primary'
    }
  }
)

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    startIcon?: ReactElement
    endIcon?: ReactElement
  }

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, variant, endIcon, startIcon, className, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : 'button'

    return (
      <Component ref={ref} className={cn(buttonVariants({ variant, className }))} {...props}>
        {startIcon ? (
          <span className="icon relative mr-spacing-sm flex items-center justify-center overflow-hidden">
            <span
              className={cn(
                'flex translate-x-0 pr-spacing-2xs',
                'transition-transform duration-500 ease-out-cubic',
                'group-hover:-translate-x-full'
              )}
            >
              {startIcon}
            </span>
            <span
              className={cn(
                'pr-spacing-xxs absolute inset-0 translate-x-full',
                'transition-transform duration-500 ease-out-cubic',
                'group-hover:translate-x-0'
              )}
            >
              {startIcon}
            </span>
          </span>
        ) : null}
        <Slottable>{children}</Slottable>
        {endIcon ? (
          <span className="icon relative ml-spacing-sm flex items-center justify-center overflow-hidden">
            <span
              className={cn(
                'flex translate-x-0 pl-spacing-2xs',
                'transition-transform duration-500 ease-out-cubic',
                'group-hover:translate-x-full'
              )}
            >
              {endIcon}
            </span>
            <span
              className={cn(
                'pl-spacing-xxs absolute inset-0 -translate-x-full',
                'transition-transform duration-500 ease-out-cubic',
                'group-hover:translate-x-0'
              )}
            >
              {endIcon}
            </span>
          </span>
        ) : null}
      </Component>
    )
  }
)

export default Button
